import { SortingOptionsType } from '@components/design/migrated/Table';
import { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import { PagesConfig, usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { revenueString, SaleType, volumeString } from '@models/Products';
import { DisplayOptions } from '@components/top-selling-products/products-table-filter/Types';

interface PersistedSortingArgsProducts {
  tableId: string;
  defaultSorting: SortingOptionsType;
  selectedSaleType: SaleType;
  setSelectedSaleType: Dispatch<SetStateAction<SaleType>>;
  selectedDisplayType: DisplayOptions;
  isViewTypeChanged: boolean;
}

export interface PersistedSortingProducts {
  sorting: SortingOptionsType;
  updateOptions: (options: SortingOptionsType) => void;
}

const getSortingOptions = (
  pagesConfig: PagesConfig,
  defaultSorting: SortingOptionsType,
  selectedDisplayType: DisplayOptions,
) => {
  if (DisplayOptions.overview === selectedDisplayType || Object.keys(pagesConfig.sortingOptionsProducts).length === 0) {
    return defaultSorting;
  }
  return pagesConfig.sortingOptionsProducts;
};

const usePersistedSortingProducts = ({
  tableId,
  defaultSorting,
  selectedSaleType,
  setSelectedSaleType,
  selectedDisplayType,
  isViewTypeChanged,
}: PersistedSortingArgsProducts): PersistedSortingProducts => {
  const pagesConfig = usePageConfig();
  const sorting: SortingOptionsType = getSortingOptions(pagesConfig, defaultSorting, selectedDisplayType);

  // if switched to table view from bar chart view, should make sure that the selectedSaleType is aligned with the headerKey
  useEffect(() => {
    if (isViewTypeChanged) {
      if (selectedSaleType === SaleType.revenue && sorting.headerKey.includes(volumeString)) {
        setSelectedSaleType(SaleType.volume);
      }
      if (selectedSaleType === SaleType.volume && sorting.headerKey.includes(revenueString)) {
        setSelectedSaleType(SaleType.revenue);
      }
    }
  }, [isViewTypeChanged]);

  if (!isViewTypeChanged) {
    if (selectedSaleType === SaleType.revenue) {
      sorting.headerKey = sorting.headerKey.replace(volumeString, revenueString);
    } else {
      sorting.headerKey = sorting.headerKey.replace(revenueString, volumeString);
    }
  }

  const updateOptions = useCallback(
    (options: SortingOptionsType) => {
      pagesConfig.setSortingOptionsProducts(options);
    },
    [tableId, pagesConfig],
  );

  return { sorting, updateOptions };
};

export default usePersistedSortingProducts;
